import React, { useState } from 'react';
import { functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { STokenLogo } from '../StreamDisplay';
import './SpinWheel.css';

const PRIZES = [
  { rarity: 'Common',    streams: 25,   color: '#d4d4d4' },
  { rarity: 'Uncommon',  streams: 50,   color: '#2ecc71' },
  { rarity: 'Rare',      streams: 125,  color: '#3498db' },
  { rarity: 'Common',    streams: 25,   color: '#d4d4d4' },
  { rarity: 'Uncommon',  streams: 50,   color: '#2ecc71' },
  { rarity: 'Epic',      streams: 250,  color: '#bf5fff' },
  { rarity: 'Rare',      streams: 125,  color: '#3498db' },
  { rarity: 'Legendary', streams: 450,  color: '#ffaa00' },
  { rarity: 'Uncommon',  streams: 50,   color: '#2ecc71' },
  { rarity: 'Ultimate',  streams: 1000, color: '#ffd700' },
];

// True odds — never displayed to users.
// Index mirrors PRIZES array above.
// Legendary ≈ 0.0001%, Ultimate ≈ 0.00001%, rest fills the remainder.
const PRIZE_ODDS = [
  0.3200,       // 0  Common
  0.1800,       // 1  Uncommon
  0.1200,       // 2  Rare
  0.3200,       // 3  Common
  0.1800,       // 4  Uncommon
  0.0397,       // 5  Epic
  0.1200,       // 6  Rare
  0.000001,     // 7  Legendary  (~0.0001 %)
  0.1800,       // 8  Uncommon
  0.0000001,    // 9  Ultimate   (~0.00001 %)
];

function pickPrizeIndex() {
  const total = PRIZE_ODDS.reduce((s, w) => s + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < PRIZE_ODDS.length; i++) {
    r -= PRIZE_ODDS[i];
    if (r <= 0) return i;
  }
  return PRIZE_ODDS.length - 1;
}

const SLICE_ANGLE = 360 / PRIZES.length;

function buildSlicePath(index, total, r = 100, cx = 150, cy = 150) {
  const startAngle = (index * (360 / total) - 90) * (Math.PI / 180);
  const endAngle   = ((index + 1) * (360 / total) - 90) * (Math.PI / 180);
  const x1 = cx + r * Math.cos(startAngle);
  const y1 = cy + r * Math.sin(startAngle);
  const x2 = cx + r * Math.cos(endAngle);
  const y2 = cy + r * Math.sin(endAngle);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z`;
}

function buildLabelTransform(index, total, r = 68, cx = 150, cy = 150) {
  const midAngle = ((index + 0.5) * (360 / total) - 90) * (Math.PI / 180);
  const x = cx + r * Math.cos(midAngle);
  const y = cy + r * Math.sin(midAngle);
  const rotate = (index + 0.5) * (360 / total);
  return { x, y, rotate };
}

export default function SpinWheel({ onClose }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult]     = useState(null);

  const COST = 450;

  const spin = async () => {
    if (spinning) return;
    setSpinning(true);
    setResult(null);

    const idx = pickPrizeIndex();

    // Correct rotation math:
    // After the wheel rotates by R degrees CW, the pointer (at 12 o'clock)
    // points at the original angle (360 - R % 360) % 360 from top.
    // Center of slice idx = (idx + 0.5) * SLICE_ANGLE from top.
    // So we need: R % 360 = (360 - (idx + 0.5) * SLICE_ANGLE) % 360
    const targetAngle = ((360 - (idx + 0.5) * SLICE_ANGLE) % 360 + 360) % 360;
    const currentAngle = ((rotation % 360) + 360) % 360;
    let delta = (targetAngle - currentAngle + 360) % 360;
    if (delta < 20) delta += 360; // avoid imperceptibly small forward jumps
    const newRotation = rotation + 5 * 360 + delta;

    setRotation(newRotation);

    await new Promise(r => setTimeout(r, 2800));

    const prize = PRIZES[idx];

    // net = prize - cost; always one atomic call
    const netStreams = prize.streams - COST;
    httpsCallable(functions, 'creditGameResult')({ game: 'spin', netStreams })
      .catch(e => console.error('[SpinWheel]', e));

    setResult(prize);
    setSpinning(false);
  };

  return (
    <div className="spin-wheel">
      <h2 className="spin-wheel__title">Spin Wheel</h2>

      <div className="spin-wheel__container">
        {/* Pointer */}
        <div className="spin-wheel__pointer" aria-hidden="true">▼</div>

        <svg
          className="spin-wheel__svg"
          viewBox="0 0 300 300"
          style={{ transform: `rotate(${rotation}deg)` }}
          aria-label="Spin wheel"
        >
          <defs>
            <filter id="sliceGlow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {PRIZES.map((prize, i) => {
            const { x, y, rotate } = buildLabelTransform(i, PRIZES.length);
            return (
              <g key={i}>
                <path
                  d={buildSlicePath(i, PRIZES.length)}
                  fill={prize.color}
                  stroke="#000000"
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="9.5"
                  fontWeight="900"
                  fill="#000000"
                  stroke="#ffffff"
                  strokeWidth="0.4"
                  paintOrder="stroke"
                  transform={`rotate(${rotate}, ${x}, ${y})`}
                >
                  {prize.streams}
                </text>
              </g>
            );
          })}

          {/* Center hub */}
          <circle cx="150" cy="150" r="18" fill="#111" stroke="rgba(0,255,136,0.5)" strokeWidth="2" />
          <circle cx="150" cy="150" r="6" fill="#00ff88" />
        </svg>
      </div>

      {result && (
        <div className="spin-wheel__result">
          <STokenLogo size={22} />
          <span>+{result.streams} — {result.rarity}!</span>
        </div>
      )}

      <button className="btn btn--primary btn--full" onClick={spin} disabled={spinning}>
        {spinning ? 'Spinning…' : <span style={{ display:'flex', alignItems:'center', gap:5, justifyContent:'center' }}>Spin · 450<STokenLogo size={15} /></span>}
      </button>
    </div>
  );
}
